/**
 * The strip every wireframe shows and no task claimed.
 *
 * Four things the room needs continuously: the world clock, the interaction
 * mode, whether the DM is keeping up, and the way into the rules.
 *
 * **The DM indicator is where the degradation ladder becomes visible.** Three
 * events were designed for it — queued, degraded, unavailable — and without a
 * surface the difference between *slow*, *degraded* and *down* is invisible to
 * the table, which is exactly the difference that decides whether a player
 * waits, shrugs, or goes and fixes something.
 *
 * The clock is rendered from what the server sends. Day and time of day are a
 * *rule* (the round length, and a calendar the ruleset lets a GM vary), so
 * this shows the server's reading and falls back to bare rounds rather than
 * dividing by numbers of its own.
 */

import { Link } from 'react-router-dom';

import type { DmStatus } from '../realtime/socket.js';

export interface WorldClockView {
  elapsedRounds: number;
  day?: number | undefined;
  timeOfDay?: string | undefined;
}

export interface TableHeaderProps {
  readonly campaignName?: string | undefined;
  readonly clock: WorldClockView | null;
  readonly mode: 'exploration' | 'combat';
  readonly round?: number | undefined;
  readonly dm: DmStatus;
  readonly connected: boolean;
}

export function TableHeader({
  campaignName,
  clock,
  mode,
  round,
  dm,
  connected,
}: TableHeaderProps) {
  return (
    <header className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-neutral-200 pb-2 text-sm dark:border-neutral-800">
      <span
        className={`inline-block h-2 w-2 shrink-0 rounded-full ${
          connected ? 'bg-emerald-500' : 'bg-neutral-400'
        }`}
        title={connected ? 'connected' : 'not connected'}
        aria-hidden
      />
      {campaignName && <span className="font-semibold">{campaignName}</span>}

      {mode === 'combat' ? (
        <span className="font-medium text-amber-800 dark:text-amber-300">
          ⚔ Combat{round === undefined ? '' : ` — round ${round}`}
        </span>
      ) : (
        <span className="text-neutral-500 dark:text-neutral-400">Exploration</span>
      )}

      {clock && (
        <span className="tabular-nums text-neutral-600 dark:text-neutral-400" title="advanced by events, never by wall-clock time">
          {clock.day !== undefined && clock.timeOfDay
            ? `Day ${clock.day} · ${clock.timeOfDay}`
            : `${clock.elapsedRounds} rounds elapsed`}
        </span>
      )}

      {/* The tooltip says what the line means in words, because "terse" and "coalesced"
          mean nothing to somebody who has not read how the narration keeps up. */}
      <span
        className="ml-auto text-xs text-neutral-500 dark:text-neutral-400"
        aria-live="polite"
        title={dmStatusHelp(dm)}
      >
        {dmStatusLine(dm)}
      </span>
      <Link to="/rules" className="text-xs underline underline-offset-2">
        Rules
      </Link>
    </header>
  );
}

/**
 * One line, and the order matters: *down* outranks *queued* outranks *slow*.
 *
 * Every one of these is a state play continues through — the engine has
 * already resolved whatever happened — so none of them says anything is
 * broken, only that the prose is behind.
 */
export function dmStatusLine(dm: DmStatus): string {
  if (dm.unavailable) {
    return `DM quiet (${dm.unavailable.replace(/_/g, ' ')}) — the engine is still running`;
  }
  if (dm.queuedAt) return `DM composing… (queued #${dm.queuedAt})`;
  if (dm.typing) return 'DM composing…';
  if (dm.degraded === 'terse') return 'DM running terse to keep up';
  if (dm.degraded === 'fast_model') return 'DM running its fast model to keep up';
  if (dm.degraded === 'coalesced') return 'DM narrating once a round to keep up';
  return '';
}

/**
 * What the status line means, for hovering it. Empty when there is no line.
 *
 * Every state here is one play continues through: the engine has already ruled on whatever
 * happened and only the prose is affected, so each says that the game is not stuck.
 */
export function dmStatusHelp(dm: DmStatus): string {
  if (dm.unavailable) {
    return 'The DM cannot write at the moment. The game carries on: the engine still rules on every action and shows the result, and the DM catches up when it can.';
  }
  if (dm.queuedAt) {
    return 'The DM is writing for another table first. Your turn is next in line; the game is not stuck.';
  }
  if (dm.typing) return 'The DM is writing the next description.';
  if (dm.degraded === 'terse') {
    return 'The DM is a little slow right now, so it is keeping its descriptions short. The game carries on and it goes back to normal when it catches up.';
  }
  if (dm.degraded === 'fast_model') {
    return 'The DM is slow right now, so it has switched to a smaller, faster model. The writing may be plainer until it catches up; the rules are unaffected.';
  }
  if (dm.degraded === 'coalesced') {
    return 'The DM is slow right now, so in a fight it describes the whole round once instead of every blow, and leaves out small flavour. The rules are unaffected.';
  }
  return '';
}
